import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { m } from 'framer-motion';
import { FiCheck, FiAlertCircle } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTheme } from '../../context/ThemeContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useTranslation } from 'react-i18next';
import { useMotionConfig } from '../../hooks/useReducedMotion';
import { STRIPE_CONFIG } from '../../config/stripe';
import ManageHeader from './ManageHeader';
import AddonsModal from '../ui/AddonsModal';
import InsuranceWarningModal from '../ui/InsuranceWarningModal';
import DeltaPaymentMethods from './DeltaPaymentMethods';
import { useDeltaPaymentReturn } from './deltaPayment';
import { computeAddonDelta, grossAddonLine, round2 } from './addonTaxDelta';

const API_BASE = STRIPE_CONFIG.apiBaseUrl || '/api';

export default function PayFlow({ token, bookingData, refreshBooking }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const { formatCurrency } = useCurrency();
  const { t } = useTranslation();
  const motion = useMotionConfig();

  const { booking, merchant, items, addons, location } = bookingData;
  const existingAddonIds = (addons || []).map(a => a.product_type_id);

  const bookingDuration = React.useMemo(() => {
    const item = (items || []).find(i => i.duration_type && i.duration_value);
    if (item) {
      const t = item.duration_type;
      const v = Math.round(Number(item.duration_value));
      const unit = t === 'hours' ? 'h'
        : (t === 'calendar_day' || t === 'multi_day' || t === 'days') ? 'd'
        : (t === 'week' || t === 'weeks') ? 'w'
        : null;
      if (unit) return { id: `${v}${unit}` };
    }
    if (booking.start_date && booking.end_date) {
      const hours = (new Date(booking.end_date) - new Date(booking.start_date)) / (1000 * 60 * 60);
      if (hours >= 168) return { id: `${Math.round(hours / 168)}w` };
      if (hours >= 24) return { id: `${Math.round(hours / 24)}d` };
      return { id: `${Math.round(hours)}h` };
    }
    return null;
  }, [items, booking.start_date, booking.end_date]);

  // Steps: 'loading' → 'addons' (if available) → 'pay' → 'success'
  const [step, setStep] = useState('loading');
  const [error, setError] = useState(null);
  const [addonsData, setAddonsData] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [isInsuranceWarningOpen, setIsInsuranceWarningOpen] = useState(false);
  const [pendingAddons, setPendingAddons] = useState(null);
  const addonsCheckedRef = React.useRef(false);

  const debug = merchant?.debug;

  // amount_paid_total includes manually recorded (e.g. cash) payments summed
  // from the payment ledger server-side; fall back to the raw amount_paid.
  const amountPaid = booking.amount_paid_total ?? booking.amount_paid ?? 0;
  // Add-on prices are NET for tax-exclusive merchants, so charge (and show)
  // the grossed-up delta — otherwise no tax is collected on the add-ons and
  // the stored total silently matches the short charge.
  const { grossDelta: addonsGrossDelta } = computeAddonDelta(booking, selectedAddons);
  const outstandingAmount = round2((booking.total_amount || 0) - amountPaid + addonsGrossDelta);

  const commitAddonsToBooking = async (addonsList = selectedAddons) => {
    if (!addonsList || addonsList.length === 0) return;
    try {
      // Base the new totals (and the existing-addon list we resend) on a FRESH
      // read of the booking rather than the page-load copy: an operator edit
      // since page load would otherwise be overwritten, and update-booking
      // deletes any addon row missing from the list we send.
      let base = booking;
      let existingAddons = addons;
      try {
        const freshRes = await fetch(`${API_BASE}/manage/booking`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (freshRes.ok) {
          const fresh = await freshRes.json();
          if (fresh?.booking) {
            base = fresh.booking;
            existingAddons = fresh.addons || [];
          }
        }
      } catch (err) {
        if (debug) console.warn('[PAY] Fresh booking read failed, using page-load copy:', err.message);
      }

      // subtotal moves by the add-on prices in their stored unit (gross when
      // tax-inclusive, net when exclusive), total_tax by the tax portion, and
      // total_amount by the gross delta. Sending all three keeps total_tax in
      // step: update-booking only writes it when the client provides it.
      const { itemsDelta, taxDelta, grossDelta } = computeAddonDelta(base, addonsList);
      // Legacy rows may carry a NULL subtotal; fall back to the gross total so
      // the delta lands on a sensible base instead of on 0.
      const baseSubtotal = base.subtotal != null
        ? (parseFloat(base.subtotal) || 0)
        : (parseFloat(base.total_amount) || 0);

      const changes = {
        addons: [
          ...(existingAddons || []).filter(a => a.quantity > 0).map(a => ({
            addonId: a.product_type_id,
            name: a.item_name || a.product_types?.product_name,
            quantity: a.quantity,
            unitPrice: a.unit_price,
            totalPrice: a.unit_price * a.quantity
          })),
          ...addonsList.map(a => ({
            addonId: a.id,
            name: a.name || a.product_name,
            quantity: a.quantity,
            unitPrice: parseFloat(a.price),
            totalPrice: parseFloat(a.price) * a.quantity
          }))
        ],
        subtotal: round2(baseSubtotal + itemsDelta),
        totalTax: round2((parseFloat(base.total_tax) || 0) + taxDelta),
        totalAmount: round2((parseFloat(base.total_amount) || 0) + grossDelta)
      };

      await fetch(`${API_BASE}/manage/update-booking`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ changes })
      });
    } catch (err) {
      if (debug) console.warn('[PAY] Failed to commit addons:', err.message);
    }
  };

  // Confirm the payment server-side and commit any add-ons. Shared by the inline
  // success path (onPaid) and the redirect-return path (useDeltaPaymentReturn).
  // When there are no add-ons there is no update-booking call to notify Make.com,
  // so we ask confirm-delta-payment to fire the notification (triggerNotification).
  const finalizePayment = async (paymentIntentId, ctx = {}, { skipConfirm = false } = {}) => {
    const addonsList = ctx.selectedAddons || [];
    const triggerNotification = addonsList.length === 0;

    if (!skipConfirm && paymentIntentId) {
      try {
        const res = await fetch(`${API_BASE}/manage/confirm-delta-payment`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentIntentId, triggerNotification })
        });
        const data = await res.json().catch(() => ({}));
        // A genuine failure (declined / abandoned redirect, e.g. an EPS payment
        // that came back unsuccessful) must not show success. `pending` (async
        // method still settling) is fine and proceeds. A network error falls
        // through — the Stripe webhook is the backstop, matching prior behavior.
        if (!res.ok && !data.pending) {
          setError(t('manage.paymentNotCompleted', 'Payment was not completed. Please try again.'));
          setStep('pay');
          return;
        }
      } catch (err) {
        if (debug) console.warn('[PAY] Failed to confirm payment server-side:', err.message);
      }
    }
    // PayPal (skipConfirm) needs no client-side notification call: the capture
    // endpoint fires the Make.com notification server-side (gated on no add-ons
    // via paypalNotifyOnCapture). The add-ons path below notifies via update-booking.

    await commitAddonsToBooking(addonsList);
    setStep('success');
  };

  // Finalize a payment that completed via an off-site redirect (MobilePay, Pay-by-Bank).
  useDeltaPaymentReturn({
    onFinalize: finalizePayment,
    onProcessing: () => setStep('loading'),
    onError: (msg) => { setError(msg); setStep('pay'); },
  });

  // Fetch available addons on mount. Show modal before the pay page if any exist.
  React.useEffect(() => {
    if (addonsCheckedRef.current) return;
    // On a redirect return, useDeltaPaymentReturn handles the page — skip addons.
    if (searchParams.get('payment_intent')) { addonsCheckedRef.current = true; return; }
    addonsCheckedRef.current = true;

    if (searchParams.get('skipAddons')) {
      setStep('pay');
      return;
    }

    const fetchAndShowAddons = async () => {
      try {
        const productTypeIds = [...new Set((items || []).filter(i => i.product_type !== 'addon').map(i => i.product_type_id))];
        if (debug) console.log('[PAY] Products:', productTypeIds, 'Existing addons:', existingAddonIds);
        if (productTypeIds.length === 0) { setStep('pay'); return; }

        const res = await fetch(`${API_BASE}/addons?locationId=${location?.id || booking.location_id}`);
        if (!res.ok) { if (debug) console.log('[PAY] Addons API failed:', res.status); setStep('pay'); return; }
        const data = await res.json();

        const allAddons = {};
        (data.addons || []).forEach(addon => {
          const hasPrice = addon.pricing_rules && addon.pricing_rules.length > 0;
          if (hasPrice && !existingAddonIds.includes(addon.id)) {
            allAddons[addon.id] = {
              ...addon,
              name: addon.product_name,
              price: addon.pricing_rules[0]?.price || 0,
              _pricingRules: addon.pricing_rules || []
            };
          }
        });

        if (debug) console.log('[PAY] Eligible addons:', Object.keys(allAddons).length);

        if (Object.keys(allAddons).length > 0) {
          setAddonsData(allAddons);
          setStep('addons');
        } else {
          setStep('pay');
        }
      } catch (err) {
        if (debug) console.log('[PAY] Addon fetch error:', err.message);
        setStep('pay');
      }
    };
    fetchAndShowAddons();
  }, []);

  const isInsuranceAddon = (addon) => {
    const category = (addon.category || '').toLowerCase();
    if (category === 'insurance') return true;
    if (addonsData && addon.id) {
      const info = addonsData[addon.id];
      if (info && (info.category || '').toLowerCase() === 'insurance') return true;
    }
    const name = (addon.name || addon.item_name || addon.product_types?.product_name || '').toLowerCase();
    return name.includes('insurance') || name.includes('versicherung');
  };

  const getTotalBikesCount = () => {
    return (items || []).filter(i => i.product_type !== 'addon').reduce((sum, i) => sum + i.quantity, 0);
  };

  const getTotalInsuranceCount = (addonsList) => {
    const existingInsurance = (addons || [])
      .filter(a => isInsuranceAddon(a))
      .reduce((sum, a) => sum + a.quantity, 0);
    const newInsurance = (addonsList || selectedAddons)
      .filter(a => isInsuranceAddon(a))
      .reduce((sum, a) => sum + a.quantity, 0);
    return existingInsurance + newInsurance;
  };

  const checkInsuranceMismatch = (addonsList) => {
    const totalBikes = getTotalBikesCount();
    const totalInsurance = getTotalInsuranceCount(addonsList);
    return totalInsurance > 0 && totalInsurance !== totalBikes;
  };

  const handleAddonsConfirm = (addonsList) => {
    if (!addonsList || addonsList.length === 0) {
      setStep('pay');
      return;
    }
    if (checkInsuranceMismatch(addonsList)) {
      setPendingAddons(addonsList);
      setTimeout(() => setIsInsuranceWarningOpen(true), 100);
    } else {
      setSelectedAddons(addonsList);
      setStep('pay');
    }
  };

  const handleInsuranceConfirm = () => {
    const totalBikes = getTotalBikesCount();
    const existingInsuranceCount = (addons || [])
      .filter(a => isInsuranceAddon(a))
      .reduce((sum, a) => sum + a.quantity, 0);

    const addonsList = pendingAddons || selectedAddons;
    const newInsuranceTarget = Math.max(0, totalBikes - existingInsuranceCount);
    const adjusted = addonsList.map(a =>
      isInsuranceAddon(a) ? { ...a, quantity: newInsuranceTarget } : a
    ).filter(a => !isInsuranceAddon(a) || a.quantity > 0);

    setSelectedAddons(adjusted);
    setIsInsuranceWarningOpen(false);
    setPendingAddons(null);
    setStep('pay');
  };

  const handleInsuranceDecline = () => {
    const addonsList = pendingAddons || selectedAddons;
    setSelectedAddons(addonsList.filter(a => !isInsuranceAddon(a)));
    setIsInsuranceWarningOpen(false);
    setPendingAddons(null);
    setStep('pay');
  };

  const handleDone = async () => {
    await refreshBooking();
    navigate(`/b/${token}`);
  };

  if (outstandingAmount <= 0 && step !== 'success' && !searchParams.get('payment_intent')) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ManageHeader merchant={merchant} token={token} showBack backLabel={t('manage.backToBooking', 'Back')} />
        <main id="main-content" className="max-w-xl mx-auto px-4 py-12 text-center">
          <m.div {...motion.scaleIn}>
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: `${theme.colors.primary.base}15` }}
            >
              <SafeIcon icon={FiCheck} className="w-10 h-10" style={{ color: theme.colors.primary.base }} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('manage.noBalanceDue', 'No Balance Due')}
            </h1>
            <p className="text-gray-500 mb-8">
              {t('manage.noBalanceDueDesc', 'This booking is fully paid.')}
            </p>
            <button
              onClick={() => navigate(`/b/${token}`)}
              className="px-8 py-3 rounded-xl font-semibold text-white transition-colors"
              style={{ backgroundColor: theme.colors.primary.base }}
            >
              {t('manage.backToBooking', 'Back to Booking')}
            </button>
          </m.div>
        </main>
      </div>
    );
  }

  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50">
        <ManageHeader merchant={merchant} token={token} showBack backLabel={t('manage.backToBooking', 'Back')} />
        <main id="main-content" className="max-w-xl mx-auto px-4 py-12 flex justify-center">
          <div role="status" className="flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
            <span className="sr-only">{t('manage.loading', 'Loading...')}</span>
          </div>
        </main>
      </div>
    );
  }

  if (step === 'addons') {
    return (
      <div className="min-h-screen bg-gray-50">
        <ManageHeader merchant={merchant} token={token} showBack backLabel={t('manage.backToBooking', 'Back')} />
        <AddonsModal
          isOpen={!isInsuranceWarningOpen}
          onClose={() => setStep('pay')}
          selectedProducts={(items || [])
            .filter(i => i.product_type !== 'addon')
            .map(i => ({
              id: i.product_type_id,
              quantity: i.quantity,
              availableAddons: Object.keys(addonsData || {})
            }))}
          onContinue={handleAddonsConfirm}
          addonsData={addonsData}
          duration={bookingDuration}
          startDate={booking.start_date}
        />
        <InsuranceWarningModal
          isOpen={isInsuranceWarningOpen}
          onClose={handleInsuranceDecline}
          onConfirm={handleInsuranceConfirm}
          bikeCount={getTotalBikesCount()}
          insuranceCount={getTotalInsuranceCount(pendingAddons)}
          addonsData={addonsData}
        />
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gray-50">
        <ManageHeader merchant={merchant} token={token} />
        <main id="main-content" className="max-w-xl mx-auto px-4 py-12 text-center">
          <m.div {...motion.scaleIn}>
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: `${theme.colors.primary.base}15` }}
            >
              <SafeIcon icon={FiCheck} className="w-10 h-10" style={{ color: theme.colors.primary.base }} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('manage.paymentSuccess', 'Payment Successful!')}
            </h1>
            <p className="text-gray-500 mb-8">
              {t('manage.paymentSuccessDesc', 'Your outstanding balance has been paid.')}
            </p>
            <button
              onClick={handleDone}
              className="px-8 py-3 rounded-xl font-semibold text-white transition-colors"
              style={{ backgroundColor: theme.colors.primary.base }}
            >
              {t('manage.backToBooking', 'Back to Booking')}
            </button>
          </m.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ManageHeader merchant={merchant} token={token} showBack backLabel={t('manage.backToBooking', 'Back')} />

      <main id="main-content" className="max-w-xl mx-auto px-4 py-6 space-y-6">
        <m.div {...motion.fadeInUp}>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {t('manage.payOutstanding', 'Pay Outstanding Balance')}
          </h1>
          <p className="text-gray-500 text-sm">
            {t('manage.payOutstandingDesc', 'Complete payment for your booking.')}
          </p>
        </m.div>

        {/* Amount summary */}
        <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="space-y-2">
            {(items || []).map(item => (
              <div key={item.id} className="flex justify-between text-sm text-gray-600">
                <span>{item.item_name || item.product_types?.product_name}{item.size ? ` (${item.size})` : ''}{item.quantity > 1 ? ` ×${item.quantity}` : ''}</span>
                <span>{formatCurrency(item.unit_price * item.quantity)}</span>
              </div>
            ))}
            {(addons || []).map(addon => (
              <div key={addon.id} className="flex justify-between text-sm text-gray-600">
                <span>{addon.item_name || addon.product_types?.product_name}{addon.quantity > 1 ? ` ×${addon.quantity}` : ''}</span>
                <span>{formatCurrency(addon.unit_price * addon.quantity)}</span>
              </div>
            ))}
            {selectedAddons.length > 0 && selectedAddons.map(addon => (
              <div key={`new-${addon.id}`} className="flex justify-between text-sm text-green-600">
                <span>{addon.name || addon.product_name}{addon.quantity > 1 ? ` ×${addon.quantity}` : ''} <span className="text-[10px] font-medium px-1 py-0.5 rounded-full bg-green-100">{t('manage.new', 'NEW')}</span></span>
                <span>{formatCurrency(grossAddonLine(booking, addon))}</span>
              </div>
            ))}
            {booking.total_discount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>{t('manage.discount', 'Discount')}{booking.discount_code ? ` (${booking.discount_code})` : ''}</span>
                <span>-{formatCurrency(booking.total_discount)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm text-gray-600">
              <span>{t('manage.total', 'Total')}</span>
              <span>{formatCurrency((booking.total_amount || 0) + addonsGrossDelta)}</span>
            </div>
            {amountPaid > 0 && (
              <div className="flex justify-between text-sm text-gray-600">
                <span>{t('manage.paid', 'Paid')}</span>
                <span>-{formatCurrency(amountPaid)}</span>
              </div>
            )}
            <div className="border-t border-gray-200 pt-3 mt-3">
              <div className="flex justify-between text-lg font-bold text-gray-900">
                <span>{t('manage.amountDue', 'Amount Due')}</span>
                <span style={{ color: theme.colors.primary.base }}>{formatCurrency(outstandingAmount)}</span>
              </div>
            </div>
          </div>
        </m.div>

        {error && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Payment methods (bucket model: card-or-bank, MobilePay, PayPal) */}
        <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('manage.payment', 'Payment')}</h2>
          <DeltaPaymentMethods
            token={token}
            merchant={merchant}
            booking={booking}
            amount={outstandingAmount}
            description="Outstanding balance payment"
            applyContext={{ selectedAddons }}
            idempotencyPrefix="pay"
            paypalNotifyOnCapture={selectedAddons.length === 0}
            onPaid={(paymentIntentId, opts) => finalizePayment(paymentIntentId, { selectedAddons }, opts)}
            onError={(msg) => setError(msg)}
          />
        </m.div>
      </main>
    </div>
  );
}
